import { Directive, ElementRef, HostListener, inject, Input, Renderer2, input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { NumberUtil } from '../util/NumberUtil';

@Directive({
	selector: '[araDecimalFormat]',
	standalone: true,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: DecimalFormatDirective,
			multi: true,
		},
	],
})
export class DecimalFormatDirective implements ControlValueAccessor {
	private readonly el = inject(ElementRef);
	private readonly renderer = inject(Renderer2);
	private readonly translocoLocaleService = inject(TranslocoLocaleService);
	private readonly numberUtil = inject(NumberUtil);

	private readonly _deDE = 'de-DE';
	private readonly _enUS = 'en-US';

	readonly allowNegative = input(true);
	@Input() decimalFractionDigitsMin = 2;
	readonly decimalFractionDigitsMax = input(2);
	readonly minValue = input<number>(undefined);
	readonly maxValue = input<number>(undefined);
	private readonly localeThousandSeparator: string = ',';
	private readonly localeDecimalSeparator: string = '.';

	private valueBeforeInput = '';

	constructor() {
		if (this.translocoLocaleService.getLocale() === this._deDE) {
			this.localeThousandSeparator = '.';
			this.localeDecimalSeparator = ',';
		}

		if (this.decimalFractionDigitsMax() < this.decimalFractionDigitsMin) {
			this.decimalFractionDigitsMin = this.decimalFractionDigitsMax();
		}
	}

	private onChange = (value: number) => {};
	private onTouched = () => {};

	writeValue(value: number): void {
		const formattedValue = this.formatDecimal(value);
		this.renderer.setProperty(this.el.nativeElement, 'value', formattedValue);
	}

	registerOnChange(fn: (value: number) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	@HostListener('keydown', ['$event'])
	onKeyDown(event: KeyboardEvent): void {
		const preventedCharacters = [this.localeThousandSeparator];

		// prevent input of decimal separator if decimalFractionDigitsMax is 0
		if (this.decimalFractionDigitsMax() === 0) {
			preventedCharacters.push(this.localeDecimalSeparator);
		}

		if (preventedCharacters.includes(event.key)) {
			event.preventDefault();
		}
	}

	@HostListener('beforeinput', ['$event'])
	onBeforeInput(event: InputEvent): void {
		this.valueBeforeInput = (<HTMLInputElement>event.target).value;
	}

	@HostListener('input', ['$event'])
	onInput(event: Event): void {
		const eventTarget = event.target as HTMLInputElement;
		const value = eventTarget.value;

		let newCursorPosition = eventTarget.selectionStart;
		if (new RegExp(/^-/).exec(value) && !this.allowNegative()) {
			// eliminate negative sign if not allowed
			this.el.nativeElement.value = value.replace(/^-/, '');
			newCursorPosition--;
		} else if (new RegExp(/^(.+)-/).exec(value)) {
			// eliminate minus sign if not at the beginning
			this.el.nativeElement.value = value.replace(/^(.+)-/, '$1');
			newCursorPosition--;
		} else if (new RegExp(/[^0-9.,-]/).exec(value)) {
			// prevent input of any character except digits, decimal separator and minus sign
			this.el.nativeElement.value = value.replaceAll(/[^0-9.,-]/g, '');
			newCursorPosition--;
		} else {
			// prevent input of more fractional digits than decimalMaxFractionDigits
			const splitValue = value.split(this.localeDecimalSeparator);
			const splitValueBeforeInput = this.valueBeforeInput.split(this.localeDecimalSeparator);
			if (splitValueBeforeInput.at(1)?.length < splitValue.at(1)?.length && splitValue.at(1)?.length > this.decimalFractionDigitsMax()) {
				const cursorShift = (event as InputEvent).data?.length ?? 0; // input can be more than one character if inserted via CTRL+V
				const valueBeforeCursorPosition = value.substring(0, eventTarget.selectionStart - cursorShift);
				const valueAfterCursorPosition = value.substring(eventTarget.selectionStart - cursorShift);
				// limit the number of fractional digits to decimalMaxFractionDigits and delete the inserted character
				this.el.nativeElement.value =
					valueBeforeCursorPosition + valueAfterCursorPosition.substring(0, this.decimalFractionDigitsMax()).replace((event as InputEvent).data, '');
				newCursorPosition = valueBeforeCursorPosition.length;
			}
		}
		// restore cursor position - not supported by input type number
		if (this.el.nativeElement.selectionStart) {
			this.el.nativeElement.selectionStart = this.el.nativeElement.selectionEnd = newCursorPosition;
		}

		let numericValue = this.parseValue(this.el.nativeElement.value);
		const minValue = this.minValue();
		if (!Number.isNaN(minValue) && numericValue < minValue) {
			numericValue = minValue;
			this.writeValue(numericValue);
		}
		const maxValue = this.maxValue();
		if (!Number.isNaN(maxValue) && numericValue > maxValue) {
			numericValue = maxValue;
			this.writeValue(numericValue);
		}

		this.onChange(numericValue);
	}

	@HostListener('blur', ['$event'])
	onBlur(event: FocusEvent): void {
		this.onTouched();
		const value = this.parseValue((<HTMLInputElement>event.target).value);
		this.writeValue(value);
	}

	private parseValue(value: string): number {
		let cleaned = value;
		if (this.translocoLocaleService.getLocale() === this._deDE) {
			cleaned = cleaned
				.replaceAll(/[^\d.,-]/g, '') // eliminate alphanumeric characters etc
				.replaceAll('.', '') // eliminate thousand separator
				.replace(',', '.');
		} else {
			cleaned = cleaned
				.replaceAll(/[^\d.,-]/g, '') // eliminate alphanumeric characters etc
				.replaceAll(',', '') // eliminate thousand separator
				.replace('.', ',');
		}

		const parsed = Number.parseFloat(cleaned);
		return Number.isNaN(parsed) ? null : parsed;
	}

	private formatDecimal(value: number | null | undefined): string {
		if (value === null || value === undefined || Number.isNaN(value)) {
			return null;
		}
		return this.numberUtil.formatDecimal(value, this.decimalFractionDigitsMin, this.decimalFractionDigitsMax());
	}
}
